import React, { useCallback, useEffect, useState } from "react";
import { FiUserCheck } from "react-icons/fi";
import useCallCenterT from "../../hooks/useCallCenterT";
import { listTicketAssignmentHistory } from "../../service/CallCenterTickets";

const formatDateTime = (value) => {
  if (!value) return "—";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value);
  return parsed.toLocaleString();
};

const sourceLabelKey = (source) => {
  switch (String(source || "").toUpperCase()) {
    case "EXCEL_UPLOAD":
      return "ccTicketAssignmentSourceExcelUpload";
    case "EXCEL_REASSIGN":
      return "ccTicketAssignmentSourceExcelReassign";
    case "MANUAL_ASSIGN":
      return "ccTicketAssignmentSourceManualAssign";
    case "MANUAL_UNASSIGN":
      return "ccTicketAssignmentSourceManualUnassign";
    case "BULK_LIST":
      return "ccTicketAssignmentSourceBulkList";
    case "BULK_API":
      return "ccTicketAssignmentSourceBulkApi";
    case "TICKET_CREATE":
      return "ccTicketAssignmentSourceTicketCreate";
    default:
      return "ccTicketAssignmentSourceUnknown";
  }
};

const actionLabelKey = (action) => {
  switch (String(action || "").toUpperCase()) {
    case "ASSIGNED":
      return "ccTicketAssignmentActionAssigned";
    case "REASSIGNED":
      return "ccTicketAssignmentActionReassigned";
    case "UNASSIGNED":
      return "ccTicketAssignmentActionUnassigned";
    default:
      return "ccTicketAssignmentActionUnknown";
  }
};

const describeEvent = (event, t) => {
  const previous = event.previousAssigneeName || t("ccUnassigned");
  const next = event.newAssigneeName || t("ccUnassigned");
  const action = String(event.action || "").toUpperCase();
  if (action === "UNASSIGNED") {
    return t("ccTicketAssignmentEventUnassigned").replace("{previous}", previous);
  }
  if (action === "ASSIGNED") {
    return t("ccTicketAssignmentEventAssigned").replace("{assignee}", next);
  }
  return t("ccTicketAssignmentEventReassigned")
    .replace("{previous}", previous)
    .replace("{next}", next);
};

const TicketAssignmentHistory = ({ ticketId, refreshKey = 0 }) => {
  const t = useCallCenterT();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const loadHistory = useCallback(async () => {
    if (!ticketId) {
      setItems([]);
      return;
    }
    setLoading(true);
    setError("");
    try {
      const response = await listTicketAssignmentHistory(ticketId);
      setItems(Array.isArray(response) ? response : []);
    } catch (err) {
      setItems([]);
      setError(err.message || t("ccTicketAssignmentHistoryLoadFailed"));
    } finally {
      setLoading(false);
    }
  }, [ticketId, t]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory, refreshKey]);

  if (!ticketId) return null;

  return (
    <div>
      <h4 className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-800">
        <FiUserCheck className="h-4 w-4 text-[#2B7FFF]" />
        {t("ccTicketAssignmentHistoryTitle")}
      </h4>
      {loading ? (
        <p className="text-sm text-slate-500">{t("ccLoading")}</p>
      ) : error ? (
        <p className="text-sm text-red-600">{error}</p>
      ) : items.length === 0 ? (
        <p className="rounded-xl border border-dashed border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-500">
          {t("ccTicketAssignmentHistoryEmpty")}
        </p>
      ) : (
        <ul className="space-y-3">
          {items.map((event) => (
            <li
              key={event.id}
              className="rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm shadow-sm"
            >
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div>
                  <p className="font-medium text-slate-800">{describeEvent(event, t)}</p>
                  <p className="mt-1 text-xs text-slate-500">
                    {t(actionLabelKey(event.action))}
                    {" · "}
                    {t(sourceLabelKey(event.source))}
                    {event.performedByName ? ` · ${event.performedByName}` : ""}
                  </p>
                </div>
                <time className="text-xs text-slate-400">{formatDateTime(event.createdAt)}</time>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default TicketAssignmentHistory;
